import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getCountFromServer,
  getDocs,
  limit as fbLimit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  startAfter,
  updateDoc,
  where,
  writeBatch,
  type DocumentData,
  type QueryConstraint,
  Timestamp,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import type {
  CommentDoc,
  ContentType,
  FollowDoc,
  FollowTarget,
  NotificationDoc,
  Page,
  RatingDoc,
  ReportDoc,
  ReportReason,
  ReportTargetKind,
  WithId,
} from '@/types';

/* -------------------------------------------------------------- ratings */

export async function getMyRating(
  storyId: string,
  uid: string,
): Promise<RatingDoc | null> {
  const snap = await getDoc(doc(db, 'stories', storyId, 'ratings', uid));
  return snap.exists() ? (snap.data() as RatingDoc) : null;
}

/** Un voto por usuario: el id del documento es el uid. */
export async function rateStory(
  storyId: string,
  uid: string,
  value: 1 | 2 | 3 | 4 | 5,
): Promise<void> {
  await setDoc(
    doc(db, 'stories', storyId, 'ratings', uid),
    { value, createdAt: serverTimestamp(), updatedAt: serverTimestamp() },
    { merge: true },
  );
}

export async function removeRating(storyId: string, uid: string): Promise<void> {
  await deleteDoc(doc(db, 'stories', storyId, 'ratings', uid));
}

/* ------------------------------------------------------------- comments */

export const commentsRef = collection(db, 'comments');

export type CommentCursor = QueryDocumentSnapshot<DocumentData>;
export type CommentPage = Page<WithId<CommentDoc>, CommentCursor>;

function toComment(snap: QueryDocumentSnapshot<DocumentData>): WithId<CommentDoc> {
  return { id: snap.id, ...(snap.data() as CommentDoc) };
}

interface ListCommentsOptions {
  storyId: string;
  /** null = comentarios a nivel historia. */
  chapterId: string | null;
  pageSize?: number;
  cursor?: CommentCursor | null;
}

/** Comentarios raíz, más recientes primero. Las respuestas se piden aparte. */
export async function listComments({
  storyId,
  chapterId,
  pageSize = 15,
  cursor = null,
}: ListCommentsOptions): Promise<CommentPage> {
  const constraints: QueryConstraint[] = [
    where('storyId', '==', storyId),
    where('chapterId', '==', chapterId),
    where('parentId', '==', null),
    where('status', '==', 'visible'),
    orderBy('createdAt', 'desc'),
  ];
  if (cursor) constraints.push(startAfter(cursor));
  constraints.push(fbLimit(pageSize + 1));

  const snap = await getDocs(query(commentsRef, ...constraints));
  const docs = snap.docs.slice(0, pageSize);

  return {
    items: docs.map(toComment),
    cursor: docs.length > 0 ? (docs[docs.length - 1] as CommentCursor) : null,
    hasMore: snap.docs.length > pageSize,
  };
}

/** Respuestas de un comentario raíz, en orden cronológico. */
export async function listReplies(parentId: string): Promise<WithId<CommentDoc>[]> {
  const snap = await getDocs(
    query(
      commentsRef,
      where('parentId', '==', parentId),
      where('status', '==', 'visible'),
      orderBy('createdAt', 'asc'),
      fbLimit(50),
    ),
  );
  return snap.docs.map(toComment);
}

interface NewComment {
  storyId: string;
  chapterId: string | null;
  parentId: string | null;
  body: string;
  author: { id: string; displayName: string; handle: string; photoURL: string | null };
}

/**
 * Crea el comentario. `isAuthorReply` nace en false a propósito: quién es la
 * autoría de la historia lo decide la Function, no el cliente.
 */
export async function createComment(input: NewComment): Promise<string> {
  const ref = doc(commentsRef);
  await setDoc(ref, {
    storyId: input.storyId,
    chapterId: input.chapterId,
    parentId: input.parentId,
    authorId: input.author.id,
    authorName: input.author.displayName,
    authorHandle: input.author.handle,
    authorPhotoURL: input.author.photoURL,
    body: input.body.trim(),
    likeCount: 0,
    replyCount: 0,
    isAuthorReply: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    status: 'visible',
  });
  return ref.id;
}

export async function updateComment(commentId: string, body: string): Promise<void> {
  await updateDoc(doc(db, 'comments', commentId), {
    body: body.trim(),
    updatedAt: serverTimestamp(),
  });
}

export async function deleteComment(commentId: string): Promise<void> {
  await deleteDoc(doc(db, 'comments', commentId));
}

/** Like con id = uid: uno por persona, por construcción. */
export async function toggleCommentLike(
  commentId: string,
  uid: string,
  liked: boolean,
): Promise<void> {
  const ref = doc(db, 'comments', commentId, 'likes', uid);
  if (liked) await deleteDoc(ref);
  else await setDoc(ref, { createdAt: serverTimestamp() });
}

export async function hasLikedComment(commentId: string, uid: string): Promise<boolean> {
  const snap = await getDoc(doc(db, 'comments', commentId, 'likes', uid));
  return snap.exists();
}

/* -------------------------------------------------------------- follows */

export const followsRef = collection(db, 'follows');

/** Id compuesto: saber si seguís algo es un getDoc, no una query. */
export function followId(followerId: string, target: FollowTarget, targetId: string): string {
  return `${followerId}_${target}_${targetId}`;
}

export async function isFollowing(
  followerId: string,
  target: FollowTarget,
  targetId: string,
): Promise<boolean> {
  const snap = await getDoc(doc(db, 'follows', followId(followerId, target, targetId)));
  return snap.exists();
}

export async function setFollowing(
  followerId: string,
  target: FollowTarget,
  targetId: string,
  contentType: ContentType | null,
  follow: boolean,
): Promise<void> {
  const ref = doc(db, 'follows', followId(followerId, target, targetId));
  if (follow) {
    await setDoc(ref, {
      followerId,
      target,
      targetId,
      contentType,
      createdAt: serverTimestamp(),
    });
  } else {
    await deleteDoc(ref);
  }
}

/** Qué sigue el usuario. Devuelve ids; las historias se traen aparte. */
export async function listFollowing(
  followerId: string,
  target: FollowTarget,
  contentType?: ContentType,
): Promise<WithId<FollowDoc>[]> {
  const constraints: QueryConstraint[] = [
    where('followerId', '==', followerId),
    where('target', '==', target),
  ];
  if (contentType) constraints.push(where('contentType', '==', contentType));
  constraints.push(orderBy('createdAt', 'desc'), fbLimit(200));

  const snap = await getDocs(query(followsRef, ...constraints));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as FollowDoc) }));
}

/* -------------------------------------------------------- notificaciones */

export async function listNotifications(uid: string, max = 50): Promise<WithId<NotificationDoc>[]> {
  const snap = await getDocs(
    query(
      collection(db, 'users', uid, 'notifications'),
      orderBy('createdAt', 'desc'),
      fbLimit(max),
    ),
  );
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as NotificationDoc) }));
}

/**
 * Cuántas sin leer, para el punto rojo del menú.
 *
 * `getCountFromServer` trae el número sin bajar los documentos: cuesta una
 * lectura cada mil, no una por notificación. Es lo que permite preguntarlo
 * en cada carga de la aplicación sin que se note en la factura.
 */
export async function countUnreadNotifications(uid: string): Promise<number> {
  const snap = await getCountFromServer(
    query(collection(db, 'users', uid, 'notifications'), where('read', '==', false)),
  );
  return snap.data().count;
}

/**
 * Quince días desde que se leyó.
 *
 * Ya cumplió su función, pero se deja una ventana por si alguien quiere volver
 * a encontrar el enlace de algo que abrió la semana pasada. Es el mismo campo
 * que fija la Function al crearla, con 90 días para las que nadie leyó.
 */
function readExpiry(): Timestamp {
  return Timestamp.fromMillis(Date.now() + 15 * 24 * 60 * 60 * 1000);
}

export async function markNotificationRead(uid: string, notificationId: string): Promise<void> {
  // Acortar la caducidad no cuesta nada extra: viaja en la misma escritura
  // que ya se hacía para marcarla leída.
  await updateDoc(doc(db, 'users', uid, 'notifications', notificationId), {
    read: true,
    expiresAt: readExpiry(),
  });
}

export async function markAllNotificationsRead(uid: string): Promise<void> {
  const snap = await getDocs(
    query(collection(db, 'users', uid, 'notifications'), where('read', '==', false), fbLimit(200)),
  );
  if (snap.empty) return;
  const batch = writeBatch(db);
  const expiresAt = readExpiry();
  snap.docs.forEach((d) => batch.update(d.ref, { read: true, expiresAt }));
  await batch.commit();
}

/**
 * Borra las notificaciones elegidas.
 *
 * Las reglas ya permitían `delete` sobre las propias desde el principio; lo
 * que faltaba era esto y el botón. Hasta ahora la única salida era esperar la
 * caducidad: 90 días sin leer, 15 desde que se abrió.
 *
 * Se manda en tandas porque un `writeBatch` aguanta 500 operaciones. Hoy la
 * lista muestra 50 y nunca se llegaría, pero el corte deja que subir ese tope
 * mañana no rompa el borrado en silencio.
 */
export async function deleteNotifications(uid: string, ids: string[]): Promise<void> {
  const TANDA = 400;
  for (let i = 0; i < ids.length; i += TANDA) {
    const batch = writeBatch(db);
    for (const id of ids.slice(i, i + TANDA)) {
      batch.delete(doc(db, 'users', uid, 'notifications', id));
    }
    await batch.commit();
  }
}

/* ------------------------------------------------------------- reportes */

export const reportsRef = collection(db, 'reports');

interface NewReport {
  targetKind: ReportTargetKind;
  targetId: string;
  storyId: string | null;
  reporterId: string;
  reason: ReportReason;
  description: string;
}

export async function createReport(input: NewReport): Promise<void> {
  const ref = doc(reportsRef);
  await setDoc(ref, {
    ...input,
    status: 'open',
    resolution: null,
    resolvedBy: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function listReports(status: ReportDoc['status']): Promise<WithId<ReportDoc>[]> {
  const snap = await getDocs(
    query(reportsRef, where('status', '==', status), orderBy('createdAt', 'desc'), fbLimit(100)),
  );
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as ReportDoc) }));
}

export async function resolveReport(
  reportId: string,
  status: ReportDoc['status'],
  resolution: string,
  adminUid: string,
): Promise<void> {
  await updateDoc(doc(db, 'reports', reportId), {
    status,
    resolution,
    resolvedBy: adminUid,
    updatedAt: serverTimestamp(),
  });
}

/* ---------------------------------------------- llamables a Functions */

/** Suma una lectura con deduplicación del lado del servidor. */
export async function incrementViews(storyId: string, sessionId: string): Promise<void> {
  const call = httpsCallable<{ storyId: string; sessionId: string }, { counted: boolean }>(
    functions,
    'incrementViews',
  );
  await call({ storyId, sessionId });
}

/** Acciones de moderación que necesitan Admin SDK. */
export async function moderateContent(input: {
  action: 'hide' | 'restore' | 'suspend';
  targetKind: ReportTargetKind | 'user';
  targetId: string;
  storyId?: string;
  days?: number;
}): Promise<void> {
  const call = httpsCallable<typeof input, { ok: boolean }>(functions, 'moderateContent');
  await call(input);
}
